import { Component, inject, OnInit, OnDestroy, signal, computed, Renderer2 } from '@angular/core';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { AlertComponent, ButtonComponent, IconComponent, InputComponent } from 'nebula-ui-kit';
import { LoaderComponent } from '@nebula-shared/ui';
import {
  AuthContent,
  AuthService,
  TranslatePipe,
  TranslationService,
  ThemeService,
  UiProfileService,
} from '@nebula-shared/data-access';
import { LoginService } from '../../../../data-access/src/lib/login.service';

@Component({
  selector: 'nebula-login',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    InputComponent,
    ButtonComponent,
    AlertComponent,
    IconComponent,
    TranslatePipe,

    LoaderComponent,
  ],
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
})
export class LoginComponent implements OnInit, OnDestroy {
  private readonly fb = inject(FormBuilder);
  private readonly auth = inject(AuthService);
  private readonly loginService = inject(LoginService);
  private readonly translation = inject(TranslationService);

  // Selector de idioma en el login (pre-sesion). Reusa el i18n; bundle publico.
  readonly languages = this.translation.availableLanguages;
  readonly activeLang = signal<string>(this.translation.currentLang);
  readonly langMenuOpen = signal<boolean>(false);
  readonly activeFlag = computed(() => {
    const lang = this.languages().find((l) => l.code === this.activeLang());
    return lang?.flagEmoji ?? '🌐';
  });

  toggleLangMenu(): void {
    this.langMenuOpen.update((v) => !v);
  }

  async selectLanguage(code: string): Promise<void> {
    this.langMenuOpen.set(false);
    await this.translation.setLanguage(code);
    this.activeLang.set(code);
  }
  private readonly router = inject(Router);
  private readonly tema = inject(ThemeService);
  private readonly uiProfile = inject(UiProfileService);

  /**
   * Lo que se muestra el aviso de preparacion.
   *
   * No es el tiempo que cuesta: es el que hace falta para que el cambio de tema se
   * lea como un paso del sistema y no como un parpadeo.
   */
  private static readonly ESPERA_DE_PREFERENCIAS = 1200;

  /** Si se esta mostrando el aviso de que se preparan las preferencias del usuario. */
  readonly preparandoSesion = signal(false);
  readonly resizeListener: () => void;
  readonly renderer = inject(Renderer2);

  form!: FormGroup;
  hidePassword = true;
  loading = signal(false);
  error = signal<{ TITLE: string; SUBTITLE: string } | null>(null);
  /** Si la pantalla se ve oscura; lo decide el dueño del tema, no esta pantalla. */
  readonly isDarkMode = this.tema.oscuro;
  isMobile = false;
  isTablet = false;

  constructor() {
    this.checkScreenSize();
    this.resizeListener = this.checkScreenSize.bind(this);
    this.form = this.fb.group({
      user: ['', [Validators.required]],
      password: ['', [Validators.required]],
    });
  }

  ngOnInit() {
    if (typeof window !== 'undefined') {
      window.addEventListener('resize', this.resizeListener);
    }
    // Nadie ha entrado todavia, de modo que aqui manda el tema del sistema
    // operativo: es lo que esa persona ya eligio para todo lo demas.
    this.tema.aplicar();
    this.avisarSiOtraPestanaCambioDeCompania();
  }

  /**
   * Se llega aqui porque otra pestaña eligio otra compania: el servidor revoca los refresh de la
   * sesion al elegir compania, y esta pestaña no adopta la compania de la otra. Se dice por que hay
   * que volver a entrar, en el idioma de la pantalla.
   */
  private avisarSiOtraPestanaCambioDeCompania(): void {
    if (typeof window === 'undefined') return;
    if (new URLSearchParams(window.location.search).get('otraPestana') !== 'true') return;
    this.error.set({
      TITLE: this.translation.translate('LOGIN.SESSION_CLOSED_OTHER_TAB.TITLE'),
      SUBTITLE: this.translation.translate('LOGIN.SESSION_CLOSED_OTHER_TAB.MESSAGE'),
    });
  }

  private showError(error: string) {
    this.error.set(null);

    setTimeout(() => {
      this.error.set({
        TITLE: this.translation.translate('LOGIN.ERR_AUTH_TITLE'),
        SUBTITLE: this.getErrorMessage(error),
      });
    });
  }
  private getErrorMessage(error: string): string {
    if (error) return error;
    return '';
  }

  private checkScreenSize(): void {
    const width = window.innerWidth;
    this.isMobile = width <= 768;
    this.isTablet = width > 768 && width <= 1024;
  }

  getError(key: string) {
    const control = this.form.get(key);

    if (!control || !control.touched) return;

    if (control.errors?.['required']) return this.translation.translate('LOGIN.REQUIRED');
    return '';
  }

  onSubmit(): void {
    this.error.set(null);

    if (this.form.valid) {
      this.loading.set(true);

      const { user, password } = this.form.value;
      const payload = {
        user: user.trim().toLowerCase(),
        password: password.trim(),
      };

      this.loginService.login(payload).subscribe({
        next: (content: AuthContent) => {
          // Contraseña vencida: el login es válido, pero se fuerza el cambio antes de entrar.
          if (content?.mustChangePassword) {
            this.auth.setMustChangePassword(true);
            this.loading.set(false);
            this.router.navigate(['/cambiar-clave']);
            return;
          }
          this.auth.setMustChangePassword(false);
          this.auth.validateTokenAndGetSession().subscribe({
            next: () => {
              this.loading.set(false);
              this.entrarConSusPreferencias();
            },
            error: (e: any) => {
              this.loading.set(false);
              this.showError(e);
            },
          });
        },
        error: (err: string) => {
          this.loading.set(false);
          this.showError(err);
        },
      });
    } else {
      this.markFormGroupTouched(this.form);
    }
  }

  private markFormGroupTouched(formGroup: FormGroup) {
    Object.values(formGroup.controls).forEach((control) => {
      control.markAsTouched();
      if (control instanceof FormGroup) {
        this.markFormGroupTouched(control);
      }
    });
  }

  toggleDarkMode(): void {
    this.tema.alternar();
  }

  /**
   * Entra ya con las preferencias de quien acaba de identificarse.
   *
   * Entre la entrada y el escritorio hay un cambio de aspecto inevitable: el login
   * se ve con el tema del sistema operativo -nadie habia entrado aun, no habia
   * preferencia que respetar- y el escritorio con el que eligio el usuario. Sin
   * nada en medio, ese cambio se lee como un fallo de pintado.
   *
   * Asi que se cuenta lo que de verdad esta pasando: se aplica su tema, se le dice
   * que se estan cargando SUS preferencias, y se entra. El aviso dura poco a
   * proposito -leer del navegador es instantaneo-, pero es el sitio donde el dia
   * que las preferencias vengan del servidor ya habra donde esperarlas, sin que
   * aparezca de golpe una espera que antes no estaba.
   */
  private entrarConSusPreferencias(): void {
    this.preparandoSesion.set(true);
    this.tema.aplicar();

    // El perfil visual es de la COMPANIA y se aplica ya, no en el proximo arranque:
    // detras de este aviso el cambio no se ve, y sin esto quien entra por primera
    // vez -o tras limpiar el navegador- tendria que recargar para verlo. Aqui vale
    // la compania que trae la sesion; al elegir otra, esa pantalla lo repite.
    this.uiProfile.aplicarVigente().subscribe();

    setTimeout(() => {
      this.preparandoSesion.set(false);
      this.router.navigate(['/layout/company']);
    }, LoginComponent.ESPERA_DE_PREFERENCIAS);
  }

  /** Navega a la página pública de recuperación de contraseña ("¿Olvidaste tu clave?"). */
  irARecuperar(): void {
    this.router.navigate(['/recuperar']);
  }

  ngOnDestroy() {
    if (typeof window !== 'undefined') {
      window.removeEventListener('resize', this.resizeListener);
    }
  }
}
